// Support-mode gating. Phase C.
//
// Direct port from spec §"Support-mode gating logic" (lines 522–671).
// These predicates decide whether an attempt counts toward mastery,
// counts toward readiness, blocks acceleration, or requires retest
// without support. The spec's master-plan tests all run through here.
//
// Spec invariants enforced:
//   - Audio-supported reading does NOT grant independent-reading mastery
//   - Oral vocabulary success does NOT grant word-reading mastery
//   - H4 (worked example) success is NOT mastery, NOT readiness
//   - High hint usage blocks acceleration
//   - Handwriting separation: handwriting errors do NOT lower reading
//     or math concept channels
//   - rawChildFacingLLM is a hard safety block

import type {
  ChildAttempt,
  AttemptWindow,
  HintLevel,
  MasteryChannel,
  SupportMode,
} from "./types";
import { inferChannel } from "./masteryClassInference";

const HINT_ORDER: Record<HintLevel, number> = {
  H0: 0,
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
};

function hasSupport(attempt: ChildAttempt, support: SupportMode): boolean {
  return attempt.supportMode.includes(support);
}

function hasAny(attempt: ChildAttempt, supports: SupportMode[]): boolean {
  return supports.some((s) => hasSupport(attempt, s));
}

/**
 * ⭐MA-630 §4b — tidy-up, NOT a behaviour fix. `multiplicativeReasoning` and
 * `measurementGeometryMastery` are maths channels and belong in this list on
 * naming grounds alone, but adding them is a PROVABLE NO-OP for
 * `canCountTowardReadiness`: that function rejects `rawChildFacingLLM`
 * globally at its top (`return false` before any channel branch runs), which
 * makes this function's own `return !attempt.rawChildFacingLLM` dead code —
 * by the time it runs, `rawChildFacingLLM` is always `false`, so the branch
 * always returns `true`, identical to the `return true` fallthrough these
 * two channels hit today. Measured: adding both channels here changes
 * `canCountTowardReadiness` output for 0 of 172,800 swept input shapes (see
 * supportModeGating.test.mjs's MA-630 §4b/clause-5 equivalence sweep). Do
 * NOT cite this edit as fixing a readiness leak — it doesn't; see MA-630 §4b.
 */
function isMathChannel(channel: MasteryChannel): boolean {
  return (
    channel === "mathConceptMastery" ||
    channel === "mathReasoningMastery" ||
    channel === "multiplicativeReasoning" ||
    channel === "measurementGeometryMastery"
  );
}

/**
 * ⭐MA-483 — did this attempt follow an "I don't know" press that PAID OUT a
 * scaffold? If so it is a HELPED answer and must never read as independent
 * mastery, on any channel.
 *
 * ⚠️This is ONE exported predicate rather than an inline
 * `supportMode.includes(...)` in the master gate, because
 * `canCountTowardMastery` is NOT the only mastery entry point.
 * `transferGating.ts:150` calls `canCountTowardIndependentReading` DIRECTLY,
 * bypassing the master gate, for exactly the four connected-text reading
 * skills (ms.read.06/10/11/13) that MA-483's own UI rung serves. A plan-review
 * agent caught that on 2026-08-04: the version of this change that guarded
 * only `canCountTowardMastery` would have let a picture-helped answer satisfy
 * `transferEvidence.passed` and thence `trueMastery`. Every gate below calls
 * THIS function, so a future sixth gate that forgets it is one grep away
 * instead of invisible.
 */
export function isPostIdkSupported(attempt: ChildAttempt): boolean {
  return hasSupport(attempt, "postIdkSupported");
}

/**
 * ⭐MA-588 — THE ONE LIVE IDK FREE-RIDE VECTOR, closed at the fold: which
 * correct attempts in this lookback are RE-ANSWERS OF AN ITEM WHOSE ANSWER
 * WAS ALREADY REVEALED to this child? A correct from 4-second-old (or
 * yesterday's) reveal memory is practice, not unsupported mastery evidence.
 *
 * How the reveal is known — no migration needed, the provenance the BACKLOG
 * item speculated about already persists: every IDK path writes the declined
 * row with `errorType:"i_dont_know"` on the same itemId, which is why
 * `wasRevealed` below keys on that errorType as well as on `answerRevealed`.
 * Reading, Mixed and Foundation (non-trace) IDK rows ALSO carry
 * `answerRevealed:true`; MathSession's IDK row and Foundation's trace IDK
 * row persist `answerRevealed:false` (corrected 2026-09-28, MA-725 RED
 * SHOULD-4 — this comment used to claim every IDK row carries true; the
 * recording inconsistency itself is carried to MA-727). MathSession's
 * wrong-tap reveal_and_advance, its coach → reveal fallback (MA-714) and a
 * coach that reaches the answer-stating apple worked example (MA-725) set
 * `answerRevealed:true` too (migration 0011). The mastery
 * fold's input (fetchRecentAttempts: last 50 rows per child+skill,
 * cross-session, chronological, merged with the local mirror) therefore
 * contains the reveal row whenever the re-serve is within the evidence
 * horizon — same-session (withBoundedRepetition re-serves under
 * MAX_REPS_PER_ITEM=2, flagged `isRepetition:true` since migration 0009) and
 * the routine cross-session re-serve alike.
 *
 * The two branches, deliberately different:
 *   - strictly-prior scan: a correct whose itemId was revealed EARLIER in
 *     the (timestamp-sorted) array is primed. Covers cross-session re-serves,
 *     which are fresh round-0 serves and carry `isRepetition:false`. Never
 *     retroactive: a reveal AFTER an honest correct cannot reach back.
 *   - ⭐the persisted `isRepetition` flag's branch: a correct FLAGGED as a
 *     within-session repeat is primed when a reveal on the same itemId exists
 *     anywhere in the SAME sessionId — order-insensitive on purpose, because
 *     mergeAttempts sorts by timestamp and equal-timestamp rows have no
 *     stable order (attemptLogger's 0015 comment), so a same-second
 *     IDK→re-serve pair can interleave in the array. Within one session a
 *     flagged repetition is by construction the LATER serve, so the
 *     order-blind check cannot retro-exclude an honest earlier correct.
 *
 * ⚠️Like MA-483's isPostIdkSupported, this is ONE exported helper because
 * canCountTowardMastery is not the only mastery entry point — but unlike
 * MA-483 it needs WINDOW context (the prior reveal lives on a different row),
 * so it cannot live inside the per-attempt gates. Consumers (one
 * definition): computeSkillMasteryState's counted-evidence bar, and
 * spacedReview.evaluateDelayedReview (slice transfer-spaced-same-item —
 * scans the skill-scoped array it already receives, so a reveal-primed
 * correct cannot pass delayed review either).
 *
 * ⚠️transferGating.evaluateTransferEvidence deliberately does NOT consume it
 * yet (MA-588 operator decision R1, 2026-09-28 RED): ms.math.09, the only
 * transfer-registered skill, has exactly ONE authored probe
 * (ME_SLICE_0907_TRANSFER), and every row of it is tagged, so one reveal on
 * it would make every later probe correct primed FOREVER — transfer
 * unpassable, trueMastery unreachable, the decision engine wedged at its
 * transfer gate. Named residual `transfer-same-item-needs-second-probe`: a
 * second authored probe + force-serve preferring an un-revealed probe +
 * `alreadyPassed` meaning a COUNTING pass, then this predicate here.
 *
 * Returns the identity-set of primed attempts (same objects the caller
 * passed), so callers filter by reference with no key invention.
 */
export function revealPrimedCorrectAttempts(
  attempts: ChildAttempt[],
): Set<ChildAttempt> {
  const wasRevealed = (a: ChildAttempt): boolean =>
    a.answerRevealed === true || a.errorType === "i_dont_know";

  // Pass 1: itemIds revealed per session (for the order-insensitive
  // isRepetition branch).
  const revealedBySession = new Map<string, Set<string>>();
  for (const a of attempts) {
    if (wasRevealed(a) && a.sessionId != null) {
      let set = revealedBySession.get(a.sessionId);
      if (!set) {
        set = new Set<string>();
        revealedBySession.set(a.sessionId, set);
      }
      set.add(a.itemId);
    }
  }

  // Pass 2: strictly-prior scan + the flagged same-session branch.
  const primed = new Set<ChildAttempt>();
  const revealedBefore = new Set<string>();
  for (const a of attempts) {
    if (a.correct) {
      const flaggedSameSessionReveal =
        a.isRepetition === true &&
        a.sessionId != null &&
        revealedBySession.get(a.sessionId)?.has(a.itemId) === true;
      if (revealedBefore.has(a.itemId) || flaggedSameSessionReveal) {
        primed.add(a);
      }
    }
    if (wasRevealed(a)) revealedBefore.add(a.itemId);
  }
  return primed;
}

/**
 * Can this single attempt count toward independent reading mastery?
 * The strictest reading gate: oral responses, audio support, picture
 * support, adult/mascot support, or H2+ hints all disqualify.
 */
export function canCountTowardIndependentReading(attempt: ChildAttempt): boolean {
  if (!attempt.correct) return false;
  // MA-483 — must be here, not only in canCountTowardMastery: transferGating.ts:150
  // calls this function directly for connected-text reading skills.
  if (isPostIdkSupported(attempt)) return false;
  if (attempt.responseChannel === "oral") return false;
  if (attempt.readAloudUsed) return false;
  if (attempt.pictureSupportUsed) return false;
  if (attempt.adultSupportUsed || attempt.mascotSupportUsed) return false;
  if (HINT_ORDER[attempt.hintLevel] > HINT_ORDER.H1) return false;
  if (attempt.contentLaunchStatus !== "launch_safe") return false;
  if (attempt.expertReviewNeeded) return false;
  return true;
}

/**
 * Can this single attempt count toward math concept mastery? H4 always
 * blocks; H2+ blocks; non-launch-safe content blocks. Numeral-formation
 * weakness does NOT invalidate (separation rule).
 */
export function canCountTowardMathConceptMastery(attempt: ChildAttempt): boolean {
  if (!attempt.correct) return false;
  // MA-483 — same reason as canCountTowardIndependentReading above: this is a
  // second directly-callable mastery entry point, not only a branch of the
  // master gate.
  if (isPostIdkSupported(attempt)) return false;
  if (hasAny(attempt, ["H4"])) return false;
  if (HINT_ORDER[attempt.hintLevel] > HINT_ORDER.H1) return false;
  if (attempt.contentLaunchStatus !== "launch_safe") return false;
  if (attempt.expertReviewNeeded) return false;
  return true;
}

/**
 * Master gate: can this attempt count toward mastery for a given
 * channel? Branches by channel because each has different support
 * tolerance (e.g. oralVocabularyMastery can accept readAloudRequired
 * but independentReading cannot).
 */
export function canCountTowardMastery(
  attempt: ChildAttempt,
  channel: MasteryChannel,
): boolean {
  if (!attempt.correct) return false;
  if (attempt.rawChildFacingLLM) return false;
  if (attempt.contentLaunchStatus !== "launch_safe") return false;
  if (attempt.expertReviewNeeded) return false;
  if (hasAny(attempt, ["H4"])) return false;
  // ⭐MA-483 — before EVERY channel branch, deliberately. Four channels
  // (handwritingMastery, numeralFormationMastery, oralVocabularyMastery,
  // listeningComprehensionMastery) never consult hintLevel at all, so a
  // hint-ordinal-based guard would have silently let a helped answer certify
  // those four. One early return covers all of them.
  if (isPostIdkSupported(attempt)) return false;

  if (channel === "handwritingMastery") {
    return attempt.handwritingRequired === true;
  }

  // ⭐MA-631 §3e — keyed on the motor fact the UI actually knows, not on a
  // string no producer ever writes. `handwritingMastery` and
  // `numeralFormationMastery` are the same idea built two different ways:
  // FoundationSession.tsx:470 sets `handwritingRequired: trace` for every
  // trace item served, but nothing anywhere ever sets `itemType:
  // "numeralFormationTask"` (that string survived only in the ItemType
  // union and this gate line) — so this channel was 0/43,200
  // producible-satisfiable while `handwritingMastery`'s otherwise-identical
  // gate was 18,720/43,200. Measured: this change alone widens
  // numeralFormationMastery's producible count to 18,720/43,200, leaving
  // the other eleven channels byte-identical over 1,008,000 shapes each,
  // and makes this limb textually identical to handwritingMastery's above —
  // deliberately: no input can separate them any more (MA-631 §3e).
  //
  // ⚠️A `|| attempt.itemType === "numeralFormationTask"` arm was considered
  // and dropped: every item §3a's mapper would ever type
  // "numeralFormationTask" ALSO sets `handwritingRequired: true`, from the
  // same boolean (contentToEngineItem.ts's trace mapping ->
  // FoundationSession's `trace = item.type === "trace"` ->
  // `handwritingRequired: trace`), so the two predicates differ on 0 of
  // 43,200 producible shapes with the arm present. The arm would have
  // bought nothing and kept a second, unreachable route to the same
  // decision. See MA-631 §3e for the full measurement.
  if (channel === "numeralFormationMastery") {
    return attempt.handwritingRequired === true;
  }

  if (channel === "oralVocabularyMastery") {
    return !attempt.rawChildFacingLLM && !hasAny(attempt, ["H4"]);
  }

  if (channel === "listeningComprehensionMastery") {
    return !hasAny(attempt, ["H4"]);
  }

  if (channel === "semanticLanguageMastery" || channel === "grammarSentenceMastery") {
    return HINT_ORDER[attempt.hintLevel] <= HINT_ORDER.H1;
  }

  if (channel === "readingDecodingMastery") {
    if (attempt.responseChannel === "oral") return false;
    if (attempt.readAloudUsed) return false;
    if (attempt.pictureSupportUsed) return false;
    if (attempt.adultSupportUsed || attempt.mascotSupportUsed) return false;
    return HINT_ORDER[attempt.hintLevel] <= HINT_ORDER.H1;
  }

  if (channel === "independentReadingMastery") {
    return canCountTowardIndependentReading(attempt);
  }

  if (channel === "mathConceptMastery") {
    return canCountTowardMathConceptMastery(attempt);
  }

  if (channel === "mathReasoningMastery") {
    if (!canCountTowardMathConceptMastery(attempt)) return false;
    return (
      attempt.itemType === "wordProblem" ||
      attempt.transferType === "representation_transfer"
    );
  }

  // ⭐MA-630 §4a — `multiplicativeReasoning` and `measurementGeometryMastery`
  // had NO branch here at all, so both fell to the terminal `return false`
  // below for every attempt, always (0/13,708,800 shapes over the full
  // exhaustive sweep) — not a branch with an unmet precondition, a channel
  // that could never certify. Both are direct-practice arithmetic; the
  // correct sibling is `mathConceptMastery`'s gate, not `mathReasoningMastery`'s
  // (which additionally demands `itemType === "wordProblem"` or
  // `transferType === "representation_transfer"` — neither is ever produced
  // by any of the seven authored skills on these two channels, so routing
  // here would reproduce the exact defect under a different line number).
  // `canCountTowardMathConceptMastery` never reads `itemType`, so the
  // content-pack item type (e.g. `count_coins`) never reaches this gate —
  // the engine `ItemType` on every attempt is `"directPractice"` regardless.
  // See MA-630 §4a for the full argument and §4d for the two channels this
  // fix deliberately does NOT touch (mathReasoningMastery,
  // numeralFormationMastery — MA-631).
  if (
    channel === "multiplicativeReasoning" ||
    channel === "measurementGeometryMastery"
  ) {
    return canCountTowardMathConceptMastery(attempt);
  }

  return false;
}

/**
 * Readiness gate: a softer version of canCountTowardMastery. Used to
 * decide if a kid is READY for the next teaching move even when
 * mastery itself isn't achieved. Supports are allowed here.
 */
export function canCountTowardReadiness(
  attempt: ChildAttempt,
  channel: MasteryChannel,
): boolean {
  if (!attempt.correct) return false;
  if (attempt.rawChildFacingLLM) return false;
  if (hasAny(attempt, ["H4"])) return false; // H4 teaches; never certifies readiness

  if (channel === "oralVocabularyMastery" || channel === "semanticLanguageMastery") {
    return true;
  }

  if (channel === "listeningComprehensionMastery") {
    return true;
  }

  if (channel === "readingDecodingMastery" || channel === "independentReadingMastery") {
    // Supported reading can show readiness, but not independent mastery.
    return !attempt.expertReviewNeeded && !attempt.rawChildFacingLLM;
  }

  if (isMathChannel(channel)) {
    return !attempt.rawChildFacingLLM;
  }

  return true;
}

/**
 * ⭐MA-786 — THE ONE PREDICATE: on which channels is a read-aloud SUPPORT?
 *
 * Read-aloud leaks the answer only where the skill measures the child reading
 * the print (word reading / decoding, and independent connected-text reading).
 * On every other channel — listening comprehension (ms.read.18/19/20, where
 * hearing the passage IS the intended path), oral vocabulary, grammar,
 * handwriting, all maths — hearing the prompt is the task or an accommodation.
 * MA-485 scoped the retest gate with this exact set; MA-786 extends it to the
 * window flag `readAloudOnlyReadingEvidence` that drives the acceleration
 * evidence (accelerationEngine BLOCK_AUDIO_SUPPORTED_READING, advisory at priority 8;
 * blocksAcceleration is test-only) AND the parent copy (AUDIO_SUPPORTED_ONLY / "success has only been seen with
 * read-aloud support"), so the consumers cannot drift.
 */
export function readAloudIsSupportForChannel(channel: MasteryChannel): boolean {
  return channel === "readingDecodingMastery" || channel === "independentReadingMastery";
}

/**
 * Window-level gate: does the attempt window contain signals that
 * should prevent acceleration (advance / compactReview)? High hint
 * rate, H4 use, same-error loop, support-only reading evidence all
 * trip this.
 */
export function blocksAcceleration(window: AttemptWindow): boolean {
  if (window.highHintRate > 0.40) return true;
  if (window.h4Used) return true;
  if (window.sameErrorLoop) return true;
  if (window.readAloudOnlyReadingEvidence) return true;
  if (window.oralOnlyReadingEvidence) return true;
  return false;
}

/**
 * Window-level gate: does the window contain any "correct with
 * support" attempt that should be retested without support before
 * being treated as mastery evidence?
 */
export function requiresRetestWithoutSupport(window: AttemptWindow): boolean {
  // ⭐MA-483 — the post-IDK check reads the RECENT window (lastRelevantAttempts,
  // usually the last 3), NOT the full relevantAttempts scan below.
  //
  // ⚠️This asymmetry is deliberate and is the whole reason it is a separate
  // statement. `relevantAttempts` is the last FIFTY attempts for this
  // (child, skill) (attemptLogger.ts:317). requiresRetestWithoutSupport fires at
  // PRIORITY 4 in adaptationDecisionEngine (:198-231) and returns early, which
  // pre-empts transfer, delayed review, enrich and advance. Scanning all fifty
  // for a post-IDK press would therefore pin a child on `independent_retest`
  // for up to fifty subsequent attempts on that skill after ONE help request —
  // a child who asked for help once in a hundred taps would be held back for
  // the rest of the day. The existing supports below keep their fifty-attempt
  // scan unchanged (that is shipped behaviour, and readAloud/adult support are
  // rare); post-IDK is expected to be COMMON by design, because MA-482 just
  // made the chip's promise honest and MA-483's rung pays out for it.
  //
  // "Recently needed help -> retest without help" is also the pedagogically
  // correct reading: the retest should follow the help, not trail it by fifty
  // items.
  if (window.lastRelevantAttempts.some((a) => a.correct && isPostIdkSupported(a))) {
    return true;
  }
  // ⭐MA-485 — READ-ALOUD DISQUALIFIES *READING*, NOT EVERYTHING.
  //
  // ⚠️THE DEFECT: this was the ONLY gate in this file that applied the
  // independent-READING support rule to EVERY mastery channel.
  // `canCountTowardMastery` already checks `readAloudUsed` on
  // readingDecodingMastery (:148) and independentReadingMastery (:78 via :155)
  // and on NONE of the other eight channels — this gate ignored that split.
  //
  // ⭐So a pre-reading five-year-old doing MATHS taps the speaker to hear the
  // question — which is the entire purpose of the speaker — and is pinned on
  // `independent_retest` for up to fifty attempts for it. Executed against the
  // real engine on ms.math.11, ten read-aloud corrects plus a passed delayed
  // review: `durableMastery=true`, `independentRate=1.00`, and still
  // `priority=4:support_retest`. Reading a maths prompt aloud to a child who
  // cannot yet read is an ACCOMMODATION, not leakage, on a maths channel.
  // `MathSession.tsx:717`, `MixedSession.tsx:435-438` and
  // `FoundationSession.tsx:375-378` all append `readAloudAllowed` on a speaker
  // tap, so this cohort is large and entirely by-design behaviour.
  //
  // ⚠️THE SPEC INVARIANT IS PRESERVED, deliberately and narrowly: read-aloud
  // still trips this gate on both reading channels, so "audio-supported reading
  // does NOT grant independent-reading mastery" (this file's own header) holds
  // unchanged. Every reading scenario is byte-identical to the previous
  // behaviour.
  //
  // ⚠️EVERY OTHER LIMB STILL TRIPS EVERY CHANNEL. The distinction is
  // ANSWER-PROXIMITY, not modality: MA-483's rung shows a picture that NAMES
  // the answer, which leaks on any channel; read-aloud is a modality that leaks
  // only on decoding. `pictureSupportUsed`, `adultSupportUsed`,
  // `mascotSupportUsed`, `hintLevel > H1` and `postIdkSupported` are unchanged.
  //
  // ⚠️WHAT THIS DELIBERATELY DOES *NOT* DO: bound the fifty-attempt scan, or
  // expire a support after K unaided attempts. MA-485 was FILED asking for that
  // and it is unsatisfiable — see docs/build/specs/MA-485-RETEST-CHANNEL-SCOPE.md
  // for the executed table. Every K below the skill's own `recentWindowSize`
  // lets a child whose correct answers are 40% carried by an answer-naming
  // picture reach `priority=8:advance`; every K at or above it fails to fix the
  // case the item was filed for. Relaxing the window also REROUTES the child to
  // a different skill mid-session via `resolveServedSkillId`'s walkback branch —
  // it is not the label-only change it looks like.
  const channel = inferChannel(window.skillId);
  const readAloudDisqualifies = readAloudIsSupportForChannel(channel);
  return window.relevantAttempts.some((attempt) => {
    if (!attempt.correct) return false;
    if (attempt.readAloudUsed && readAloudDisqualifies) return true;
    if (attempt.pictureSupportUsed) return true;
    if (attempt.adultSupportUsed || attempt.mascotSupportUsed) return true;
    return HINT_ORDER[attempt.hintLevel] > HINT_ORDER.H1;
  });
}

/**
 * MA-485 slice b — narrow, correctness-filtered signal for the PARENT-
 * FACING COPY only: did a picture/post-IDK-supported attempt actually
 * contribute to `requiresRetestWithoutSupport` firing?
 *
 * ⚠️Deliberately NOT `summarizeSupportMode`: that helper resolves ONE
 * window-wide value via a fixed adult > mascot > audio > visual >
 * scaffolded priority ladder, with no `.correct` filter and no scoping to
 * which attempt actually tripped the gate. Reusing it here would mislabel
 * a picture-caused retest as "audio-supported" whenever the window also
 * contains an unrelated (and near-ubiquitous, MA-485's own decision log)
 * read-aloud tap, or claim a picture caused a retest that an unrelated
 * INCORRECT picture-supported attempt did not actually cause (picture
 * support only matters on a correct attempt — see `requiresRetestWithout-
 * Support` above, which this mirrors exactly for its two picture/post-IDK
 * limbs only, omitting hint/adult/mascot/audio on purpose).
 *
 * Read-only derived signal; does not change `requiresRetestWithoutSupport`
 * or any gating/window behaviour.
 */
export function hasPictureOrPostIdkSupportEvidence(window: AttemptWindow): boolean {
  if (window.lastRelevantAttempts.some((a) => a.correct && isPostIdkSupported(a))) {
    return true;
  }
  return window.relevantAttempts.some((a) => a.correct && a.pictureSupportUsed);
}

/**
 * ⭐MA-816 — the PICTURE half of `hasPictureOrPostIdkSupportEvidence`: a
 * picture or sound box was actually SHOWN on a correct attempt. A post-IDK
 * answer counts only when the scaffold paid out was a picture
 * (`deriveAttemptSupportShape` adds `pictureSupported` iff the item's
 * `supports` include "PIC"), so `pictureSupportUsed` alone is the truth.
 * Gates the parent sentence "…picture or sound-box support that named the
 * answer".
 */
export function hasPictureSupportEvidence(window: AttemptWindow): boolean {
  return window.relevantAttempts.some((a) => a.correct && a.pictureSupportUsed);
}

/**
 * ⭐MA-816 — the POST-IDK half, with no claim about a picture: a correct
 * answer among the last attempts followed an "I don't know" press (hint brought up).
 * `hasPictureOrPostIdkSupportEvidence` stays the union (old behaviour);
 * this is the limb the engine labels `POST_IDK_RETEST` when
 * `hasPictureSupportEvidence` is false.
 */
export function hasPostIdkCorrectEvidence(window: AttemptWindow): boolean {
  return window.lastRelevantAttempts.some((a) => a.correct && isPostIdkSupported(a));
}
